import { useState, useEffect, useCallback, useRef } from 'react';

const STORAGE_KEY = 'pium-collab:projects';
const CURRENT_KEY = 'pium-collab:current';
const SNAPS_KEY   = 'pium-collab:snapshots';
const MAX_SNAPS = 20;
const SNAP_INTERVAL_MS = 2 * 60 * 1000; // 2분

const SEED_DATA = {
  cards: [
    { id: 1, type: 'note', x: 80,  y: 110, w: 200, h: 110, title: '콘셉트 방향',  content: '미래과학 + 체험형\n감성 스토리텔링 중심', color: '#C9A84C', reactions: { '👍': 2 }, comments: [] },
    { id: 2, type: 'task', x: 320, y: 90,  w: 200, h: 110, title: '수정 #2 진행중', content: '예산표 항목 추가\n박대리 담당',           color: '#4A8FD4', reactions: {}, comments: [] },
    { id: 3, type: 'file', x: 560, y: 120, w: 200, h: 110, title: '최종본 v3 확인', content: '전북과학교육원_v3.pptx',                color: '#52A070', reactions: { '✅': 3 }, comments: [] },
  ],
  areas: [
    { id: 1, x: 60,  y: 70,  w: 480, h: 200, name: '콘셉트 영역', color: '#C9A84C' },
    { id: 2, x: 100, y: 290, w: 540, h: 170, name: '실행 트랙',   color: '#4A8FD4' },
  ],
  dates: [
    { id: 1, x: 880, y: 70, w: 220, h: 130, label: '제안서 납품', date: '2026-07-05', color: '#C05252' },
  ],
  paths: [],
};

function makeId() { return 'p_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7); }

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Array.isArray(data)) {
      data.forEach(p => {
        if (p?.data?.paths) {
          p.data.paths = p.data.paths.filter(pa =>
            pa && Array.isArray(pa.points) && pa.points.length >= 2 &&
            pa.points.every(pt => pt && typeof pt.x === 'number' && typeof pt.y === 'number' && isFinite(pt.x) && isFinite(pt.y))
          );
        }
      });
    }
    return data;
  } catch { return null; }
}
function save(projects) { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(projects)); } catch {} }

function loadSnaps() {
  try {
    const raw = localStorage.getItem(SNAPS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}
function saveSnaps(snaps) { try { localStorage.setItem(SNAPS_KEY, JSON.stringify(snaps)); } catch (e) {
  // 용량 초과 시 가장 오래된 것부터 제거
  if (snaps.length > 1) saveSnaps(snaps.slice(0, -1));
}}

function defaultProjects() {
  return [{
    id: makeId(),
    name: '전북특별자치도교육청과학교육원 제안서',
    sharePointFolder: '/04-제안/전북과학교육원',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    data: SEED_DATA,
  }];
}

export function useProject() {
  const [projects, setProjects] = useState(() => load() || defaultProjects());
  const [currentId, setCurrentId] = useState(() => {
    try { return localStorage.getItem(CURRENT_KEY) || ''; } catch { return ''; }
  });
  const [snapshots, setSnapshots] = useState(loadSnaps);
  const lastChangeRef = useRef(Date.now());
  const lastSnapTsRef = useRef(0);

  useEffect(() => {
    if (!currentId && projects.length > 0) setCurrentId(projects[0].id);
  }, [currentId, projects]);

  // 영속화 + 변경 시점 기록
  useEffect(() => {
    const t = setTimeout(() => {
      try { save(projects); } catch (e) { console.warn('localStorage save failed:', e); }
      lastChangeRef.current = Date.now();
    }, 200);
    return () => clearTimeout(t);
  }, [projects]);

  useEffect(() => {
    try { localStorage.setItem(CURRENT_KEY, currentId); } catch {}
  }, [currentId]);

  // ── 자동 스냅샷 (2분마다, 변경이 있었을 때만) ──
  useEffect(() => {
    const tick = () => {
      // 마지막 스냅 이후 변경이 있었을 때만
      if (lastChangeRef.current > lastSnapTsRef.current) {
        const snap = {
          ts: Date.now(),
          label: '자동',
          projects: JSON.parse(JSON.stringify(projects)),
        };
        setSnapshots(prev => {
          const next = [snap, ...prev].slice(0, MAX_SNAPS);
          saveSnaps(next);
          return next;
        });
        lastSnapTsRef.current = Date.now();
      }
    };
    const interval = setInterval(tick, SNAP_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [projects]);

  // 수동 스냅샷
  const takeSnapshot = useCallback((label = '수동') => {
    const snap = {
      ts: Date.now(),
      label,
      projects: JSON.parse(JSON.stringify(projects)),
    };
    setSnapshots(prev => {
      const next = [snap, ...prev].slice(0, MAX_SNAPS);
      saveSnaps(next);
      return next;
    });
    lastSnapTsRef.current = Date.now();
    return snap;
  }, [projects]);

  const restoreSnapshot = useCallback((ts) => {
    const snap = snapshots.find(s => s.ts === ts);
    if (!snap) return false;
    // 현재 상태를 "복원 전" 스냅으로 백업 (실수 대비)
    const safetyNet = {
      ts: Date.now(),
      label: '복원 전 자동',
      projects: JSON.parse(JSON.stringify(projects)),
    };
    setSnapshots(prev => {
      const next = [safetyNet, ...prev].slice(0, MAX_SNAPS);
      saveSnaps(next);
      return next;
    });
    setProjects(JSON.parse(JSON.stringify(snap.projects)));
    return true;
  }, [snapshots, projects]);

  const deleteSnapshot = useCallback((ts) => {
    setSnapshots(prev => {
      const next = prev.filter(s => s.ts !== ts);
      saveSnaps(next);
      return next;
    });
  }, []);

  const clearAllSnapshots = useCallback(() => {
    setSnapshots([]);
    saveSnaps([]);
  }, []);

  const current = projects.find(p => p.id === currentId) || projects[0];

  const setData = useCallback((updaterOrValue) => {
    setProjects(prev => prev.map(p => {
      if (p.id !== currentId) return p;
      const newData = typeof updaterOrValue === 'function' ? updaterOrValue(p.data || {}) : updaterOrValue;
      return { ...p, data: newData, updatedAt: new Date().toISOString() };
    }));
  }, [currentId]);

  const createProject = useCallback((name, sharePointFolder = '') => {
    const np = {
      id: makeId(), name: name || '새 제안 프로젝트', sharePointFolder,
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
      data: { cards: [], areas: [], dates: [], paths: [] },
    };
    setProjects(prev => [...prev, np]);
    setCurrentId(np.id);
    return np;
  }, []);

  const duplicateProject = useCallback((id) => {
    const src = projects.find(p => p.id === id);
    if (!src) return;
    const np = { ...JSON.parse(JSON.stringify(src)), id: makeId(), name: src.name + ' (복사)', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
    setProjects(prev => [...prev, np]);
    setCurrentId(np.id);
  }, [projects]);

  const renameProject = useCallback((id, name) => {
    setProjects(prev => prev.map(p => p.id === id ? { ...p, name, updatedAt: new Date().toISOString() } : p));
  }, []);

  const updateProjectMeta = useCallback((id, partial) => {
    setProjects(prev => prev.map(p => p.id === id ? { ...p, ...partial, updatedAt: new Date().toISOString() } : p));
  }, []);

  const deleteProject = useCallback((id) => {
    // 삭제 직전 자동 스냅샷
    takeSnapshot('삭제 직전');
    setProjects(prev => {
      const next = prev.filter(p => p.id !== id);
      if (id === currentId && next.length > 0) setCurrentId(next[0].id);
      return next.length > 0 ? next : defaultProjects();
    });
  }, [currentId, takeSnapshot]);

  const exportBackup = useCallback((projectId = null) => {
    const payload = {
      version: '0.9.8',
      exportedAt: new Date().toISOString(),
      projects: projectId ? projects.filter(p => p.id === projectId) : projects,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pium-collab-backup-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }, [projects]);

  const importBackup = useCallback(async (file) => {
    if (!file) return { ok: false, msg: '파일 없음' };
    try {
      // 불러오기 직전 안전 스냅샷
      takeSnapshot('불러오기 직전');
      const text = await file.text();
      const json = JSON.parse(text);
      if (!json.projects || !Array.isArray(json.projects)) return { ok: false, msg: '백업 형식 오류' };
      const existing = new Set(projects.map(p => p.id));
      const imported = json.projects.map(p => existing.has(p.id) ? { ...p, id: makeId(), name: p.name + ' (가져옴)' } : p);
      setProjects(prev => [...prev, ...imported]);
      return { ok: true, count: imported.length };
    } catch (e) {
      return { ok: false, msg: e.message };
    }
  }, [projects, takeSnapshot]);

  const search = useCallback((q) => {
    if (!q) return [];
    const lq = q.toLowerCase();
    return projects.filter(p => {
      if (p.name.toLowerCase().includes(lq)) return true;
      const d = p.data || {};
      return (d.cards || []).some(c => (c.title + ' ' + c.content).toLowerCase().includes(lq))
          || (d.dates || []).some(dt => dt.label.toLowerCase().includes(lq));
    });
  }, [projects]);

  return {
    projects, current, currentId, setCurrentId, setData,
    createProject, duplicateProject, renameProject, updateProjectMeta, deleteProject,
    exportBackup, importBackup, search,
    snapshots, takeSnapshot, restoreSnapshot, deleteSnapshot, clearAllSnapshots,
  };
}
